/**
 * A template's example data, written out as the spreadsheet that would fill it in.
 *
 * The inverse of `project`. A starter shows finished figures before anybody has
 * brought anything in, and the next question is always "what does my spreadsheet
 * have to look like?" — which the gallery's one-line `wants` can only gesture at.
 * The honest answer is a file: this document's own figures, in the shape an upload
 * takes, so somebody can open it, see the columns, and replace the rows with theirs.
 *
 * The shape is the one `project` reads, and the one "Type the details in" writes:
 *
 * - **one row per item of the list**, because an upload is one table and the list
 *   is what varies from row to row;
 * - **single values repeated down every row**, not left blank after the first,
 *   because a constant column is what tells the matcher the file is one document —
 *   a column that holds a value once and blanks after reads as several;
 * - **headings are the labels a person reads**, with a group's name in front
 *   ("Bill to name"), because they are what the matcher compares a heading
 *   against, and the file is only worth downloading if it matches with nothing
 *   to ask. `starters.test.ts` holds every starter to that.
 *
 * Values are written the way a spreadsheet shows them rather than the way they are
 * stored: a VAT rate is `20%`, not `0.2`, which a person would read as a fifth of
 * a per cent. Dates stay ISO, which no locale misreads and every spreadsheet opens
 * as a date.
 */
import type { DataSource, FieldType } from "@broadpaper/core";
import { requirementsOf } from "./requirements.js";

export interface SampleSheet {
  headers: string[];
  rows: string[][];
}

export function sampleSheet(source: DataSource, data: unknown): SampleSheet {
  const requirements = requirementsOf(source);
  // The first list only. One table fills one list: `project` reads every list from
  // the same rows, so a second would be filled with the first one's items.
  const list = requirements.find((r) => r.type === "array");
  const items = list ? requirements.filter((r) => r.itemOf === list.path) : [];
  const singles = requirements.filter((r) => r.type !== "array" && !r.itemOf);

  const singleHeadings = singles.map((r) => heading(r.label));
  const taken = new Set(singleHeadings.map((h) => h.toLowerCase()));
  // An item's own label where it is unambiguous — "Description", not "Lines
  // description" — and the list's name in front only where a single value already
  // uses the word, as a client report's "Name" and its lines' "Name" would.
  const itemHeadings = items.map((r) => {
    const leaf = r.label.split(" › ").pop()!;
    return taken.has(leaf.toLowerCase()) ? heading(r.label) : leaf;
  });

  const root = (data ?? {}) as Record<string, unknown>;
  // A single value the sample leaves out is written from the field's own example.
  // Some starters leave a figure for the document to work out — a quote's total,
  // a statement's closing balance — and a file without that column teaches
  // somebody that it does not exist; worse, the statement's running Balance then
  // fills the closing balance from its first row. The examples agree with the
  // figures the document works out, and `starters.test.ts` draws both to prove it.
  const single = singles.map((r) => cell(read(root, r.path) ?? r.example, r.type));
  const listed = list ? read(root, list.path) : undefined;
  const lines = Array.isArray(listed) && listed.length ? (listed as Array<Record<string, unknown>>) : [{}];

  const headers = [...singleHeadings, ...itemHeadings];
  const rows = lines.map((line) => [...single, ...items.map((r) => cell(line?.[r.path.slice(list!.path.length + 1)], r.type))]);

  // A column with nothing in it at all is left out rather than written blank. A
  // blank column is still matched, and a matched column with nothing in it prints
  // the field's placeholder rather than nothing.
  const kept = headers.map((_, i) => rows.some((row) => row[i] !== ""));
  return {
    headers: headers.filter((_, i) => kept[i]),
    rows: rows.map((row) => row.filter((_, i) => kept[i]))
  };
}

/**
 * The sheet as CSV, with a byte-order mark.
 *
 * The mark is for Excel, which opens a CSV without one in the machine's legacy code
 * page and turns `£` into `Â£` — the first thing anybody would see on opening it.
 * Ingest strips it on the way back in.
 */
export function sampleCsv(sheet: SampleSheet): string {
  const line = (values: string[]) => values.map(quote).join(",");
  return `${BYTE_ORDER_MARK}${[line(sheet.headers), ...sheet.rows.map(line)].join("\r\n")}\r\n`;
}

/** Built from its code point, because the character itself is invisible in an editor. */
const BYTE_ORDER_MARK = String.fromCharCode(0xfeff);

/**
 * "Bill to › Name" as a heading: "Bill to name".
 *
 * A label that already starts with its group's name keeps just the label, so a
 * statement's "Account › Account reference" is "Account reference" rather than
 * saying it twice.
 */
function heading(label: string): string {
  const parts = label.split(" › ");
  const out: string[] = [];
  for (const part of parts) {
    const previous = out[out.length - 1];
    if (previous && part.toLowerCase().startsWith(previous.toLowerCase())) out[out.length - 1] = part;
    else out.push(out.length ? part.charAt(0).toLowerCase() + part.slice(1) : part);
  }
  return out.join(" ");
}

function read(root: Record<string, unknown>, path: string): unknown {
  let cursor: unknown = root;
  for (const key of path.split(".")) {
    if (typeof cursor !== "object" || cursor === null) return undefined;
    cursor = (cursor as Record<string, unknown>)[key];
  }
  return cursor;
}

function cell(value: unknown, type: FieldType): string {
  if (value === null || value === undefined) return "";
  if (typeof value === "number") {
    if (type === "percent") return `${Math.round(value * 100 * 1e4) / 1e4}%`;
    if (type === "currency") return value.toFixed(2);
    return String(value);
  }
  if (typeof value === "boolean") return value ? "Yes" : "No";
  return String(value);
}

function quote(value: string): string {
  return /[",\r\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;
}
